import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fly/core';
import { toastService } from '@fly/shared';
import { Checkbox } from '@radix-ui/themes';

import { useDistributeArtifactMutation } from '../../../../store/registry.service';
import type { DistributionFormRow } from '../DistributionForm/distributionFormKeys';

export const SKIP_DISTRIBUTION_CONFIRMATION_KEY =
  'fly-registry-skip-distribution-confirmation';

function persistSkipDistributionConfirmation() {
  try {
    localStorage.setItem(SKIP_DISTRIBUTION_CONFIRMATION_KEY, 'true');
  } catch {
    // Ignore storage failures and proceed with the distribution flow.
  }
}

export interface DistributionConfirmationProps {
  /** Package name shown in the body (distribute flow from `DistributionForm`). */
  onCancel: () => void;
  /** When `distributeRow` is omitted, invoked after optional skip persistence (e.g. tests). */
  onConfirm?: () => void;
  /** When set, Distribute runs the RTK mutation and calls `onDistributeComplete` on success. */
  distributeRow?: DistributionFormRow;
  onDistributeComplete?: () => void;
}

export const DistributionConfirmation: React.FC<
  DistributionConfirmationProps
> = ({ onCancel, onConfirm, distributeRow, onDistributeComplete }) => {
  const { t } = useTranslation();
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [distributeArtifact, { isLoading }] = useDistributeArtifactMutation();

  const handleDistributeClick = async () => {
    if (dontAskAgain) {
      persistSkipDistributionConfirmation();
    }
    if (distributeRow) {
      try {
        await distributeArtifact({
          packageName: distributeRow.artifactName,
          packageVersion: distributeRow.versionName,
          packageType: distributeRow.packageType,
        }).unwrap();
        toastService.success(
          t('app.dashboard.artifacts.distribution_form.distribute_success', {
            defaultValue: 'Artifact distributed successfully',
          })
        );
        onDistributeComplete?.();
      } catch {
        toastService.error(
          t('app.dashboard.artifacts.distribution_form.error_generic', {
            defaultValue:
              "We couldn't update the distribution. Please try again.",
          })
        );
      }
    } else {
      onConfirm?.();
    }
  };

  const resolvedArtifactName = distributeRow
    ? `${distributeRow.artifactName} - #${distributeRow.versionName}`
    : null;
  const descriptionBody = resolvedArtifactName
    ? t(
        'app.dashboard.artifacts.distribution_form.distribution_confirmation.description',
        {
          artifactName: resolvedArtifactName,
          defaultValue: '{{artifactName}} - will be distributed to the public',
        }
      )
    : t(
        'app.dashboard.artifacts.distribution_form.distribution_confirmation.description_anonymous',
        {
          defaultValue:
            'Making this artifact public will make it accessible to anyone. You can revoke distribution later.',
        }
      );

  return (
    <div data-testid='distribution-confirm-panel' className='mt-[-20px]!'>
      <Text
        data-testid='distribution-confirm-description'
        size='2'
        color='neutral'
        className='text-(--text-color)'
      >
        {descriptionBody}
      </Text>
      <div className='mt-fly-4 mb-fly-4 flex flex-row items-center justify-end gap-fly-3'>
        <Text
          as='label'
          size='2'
          color='neutral'
          className='font-semibold flex flex-1 cursor-pointer'
        >
          <Checkbox
            data-testid='distribution-confirm-do-not-ask'
            checked={dontAskAgain}
            onCheckedChange={(checked) => setDontAskAgain(Boolean(checked))}
            className='mr-fly-3'
            color='indigo'
          />
          {t(
            'app.dashboard.artifacts.distribution_form.distribution_confirmation.dont_ask_again',
            {
              defaultValue: "Don't ask me again",
            }
          )}
        </Text>
      </div>
      <div className='flex justify-end gap-fly-3'>
        <Button
          data-testid='distribution-confirm-cancel'
          size='2'
          variant='soft'
          color='neutral'
          onClick={onCancel}
        >
          {t(
            'app.dashboard.artifacts.distribution_form.distribution_confirmation.cancel',
            {
              defaultValue: 'Cancel',
            }
          )}
        </Button>
        <Button
          data-testid='distribution-confirm-submit'
          size='2'
          variant='solid'
          color='error'
          loading={Boolean(distributeRow) && isLoading}
          onClick={() => {
            void handleDistributeClick();
          }}
        >
          {t(
            'app.dashboard.artifacts.distribution_form.distribution_confirmation.distribute',
            {
              defaultValue: 'Distribute',
            }
          )}
        </Button>
      </div>
    </div>
  );
};
